import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, Plus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router';

import { FundSelect } from '@/components/shared/fund-select';
import { CONTRIBUTION_METHODS } from '@/lib/contribution-methods';
import { EntityCombobox, type EntityOption } from '@/components/shared/entity-combobox';
import { PageHeader } from '@/components/shared/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { MultiSelectListbox } from '@/components/ui/multi-select-listbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';

import {
  CUSTOM_REPORT_SOURCE_KEYS,
  CUSTOM_REPORT_COLUMN_ALIASES,
  getCustomReportSource,
  operatorsForField,
  type CustomReportFieldDefinition,
  type CustomReportSourceKey,
} from './custom-report-sources';
import { ReportsSubnav } from './reports-subnav';
import type { SeededCustomReportDraft } from './seeded-report-custom-draft';

interface FilterRow {
  field: string;
  operator: string;
  value: string;
}

const MemberFilterValue = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => {
  const { t } = useTranslation('reports');
  const [query, setQuery] = useState('');
  const members = useQuery({
    queryKey: ['people', 'members', 'custom-report-filter', query],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; firstName: string; lastName: string }> }>(
        `/people/members?pageSize=20${query ? `&q=${encodeURIComponent(query)}` : ''}`,
      ),
  });
  const selectedMember = useQuery({
    queryKey: ['people', 'members', 'custom-report-filter-selected', value],
    queryFn: () => api.get<{ id: string; firstName: string; lastName: string }>(`/people/members/${value}`),
    enabled: Boolean(value),
  });
  const options = useMemo(
    (): EntityOption[] =>
      (members.data?.data ?? []).map((member) => ({
        id: member.id,
        label: `${member.firstName} ${member.lastName}`.trim(),
      })),
    [members.data?.data],
  );
  const selected = useMemo((): EntityOption | null => {
    if (!value) return null;
    const fromResults = options.find((option) => option.id === value);
    if (fromResults) return fromResults;
    if (selectedMember.data) {
      return {
        id: selectedMember.data.id,
        label: `${selectedMember.data.firstName} ${selectedMember.data.lastName}`.trim(),
      };
    }
    return null;
  }, [options, selectedMember.data, value]);

  return (
    <EntityCombobox
      value={value || null}
      onChange={(next) => onChange(next ?? '')}
      options={options}
      selected={selected}
      onQueryChange={setQuery}
      loading={members.isFetching}
      placeholder={t('custom.filterValue')}
    />
  );
};

interface CustomReportDefinition {
  id: string;
  name: string;
  description: string | null;
  objectSource: CustomReportSourceKey;
  columns: string[];
  filters: FilterRow[];
  sortOrder: { field: string; direction: 'asc' | 'desc' } | null;
}

const ENUM_OPTIONS: Record<string, { value: string; labelKey: string }[]> = {
  contribution_type: [
    'tithe',
    'offering',
    'first_fruits',
    'thanksgiving',
    'project',
    'other',
  ].map((value) => ({ value, labelKey: `custom.enums.contribution_type.${value}` })),
  contribution_method: CONTRIBUTION_METHODS.map((value) => ({
    value,
    labelKey: `custom.enums.contribution_method.${value}`,
  })),
  member_type: ['member', 'first_timer', 'visitor', 'associate'].map((value) => ({
    value,
    labelKey: `custom.enums.member_type.${value}`,
  })),
  member_status: ['active', 'inactive', 'transferred', 'deceased'].map((value) => ({
    value,
    labelKey: `custom.enums.member_status.${value}`,
  })),
  gender: ['male', 'female', 'unknown'].map((value) => ({
    value,
    labelKey: `custom.enums.gender.${value}`,
  })),
  expense_status: ['draft', 'pending_approval', 'approved', 'paid', 'void'].map((value) => ({
    value,
    labelKey: `custom.enums.expense_status.${value}`,
  })),
  expense_method: CONTRIBUTION_METHODS.map((value) => ({
    value,
    labelKey: `custom.enums.expense_method.${value}`,
  })),
  expense_payee_kind: ['member', 'vendor', 'other'].map((value) => ({
    value,
    labelKey: `custom.enums.expense_payee_kind.${value}`,
  })),
  pledge_status: ['active', 'fulfilled', 'cancelled', 'lapsed'].map((value) => ({
    value,
    labelKey: `custom.enums.pledge_status.${value}`,
  })),
  pledge_frequency: ['one_off', 'weekly', 'monthly', 'quarterly', 'annual'].map((value) => ({
    value,
    labelKey: `custom.enums.pledge_frequency.${value}`,
  })),
  project_status: ['draft', 'active', 'funded', 'closed'].map((value) => ({
    value,
    labelKey: `custom.enums.project_status.${value}`,
  })),
  welfare_status: ['submitted', 'under_review', 'approved', 'rejected', 'disbursed', 'voided'].map(
    (value) => ({
      value,
      labelKey: `custom.enums.welfare_status.${value}`,
    }),
  ),
  group_type: ['manual', 'template'].map((value) => ({
    value,
    labelKey: `custom.enums.group_type.${value}`,
  })),
  group_category: ['committee', 'group'].map((value) => ({
    value,
    labelKey: `custom.enums.group_category.${value}`,
  })),
  check_in_method: ['qr', 'gps', 'manual'].map((value) => ({
    value,
    labelKey: `custom.enums.check_in_method.${value}`,
  })),
  attendance_session_method: ['qr', 'gps', 'manual'].map((value) => ({
    value,
    labelKey: `custom.enums.attendance_session_method.${value}`,
  })),
  attendance_session_status: ['open', 'closed'].map((value) => ({
    value,
    labelKey: `custom.enums.attendance_session_status.${value}`,
  })),
  pastoral_visit_kind: ['home', 'hospital', 'workplace', 'other'].map((value) => ({
    value,
    labelKey: `custom.enums.pastoral_visit_kind.${value}`,
  })),
  pastoral_appointment_kind: ['visit', 'counseling', 'general', 'follow_up'].map((value) => ({
    value,
    labelKey: `custom.enums.pastoral_appointment_kind.${value}`,
  })),
  pastoral_appointment_status: ['scheduled', 'completed', 'cancelled', 'no_show'].map((value) => ({
    value,
    labelKey: `custom.enums.pastoral_appointment_status.${value}`,
  })),
  prayer_category: ['health', 'family', 'finance', 'spiritual', 'thanksgiving', 'other'].map(
    (value) => ({
      value,
      labelKey: `custom.enums.prayer_category.${value}`,
    }),
  ),
  prayer_status: ['new', 'assigned', 'praying', 'answered', 'archived'].map((value) => ({
    value,
    labelKey: `custom.enums.prayer_status.${value}`,
  })),
  campaign_status: [
    'draft',
    'scheduled',
    'sending',
    'sent',
    'partially_failed',
    'cancelled',
    'failed',
  ].map((value) => ({
    value,
    labelKey: `custom.enums.campaign_status.${value}`,
  })),
  message_audience_type: [
    'whole_church',
    'department',
    'unit',
    'group',
    'committee',
    'auto_group',
    'council',
    'filter',
    'manual',
    'list',
  ].map(
    (value) => ({
      value,
      labelKey: `custom.enums.message_audience_type.${value}`,
    }),
  ),
  asset_kind: ['building', 'furniture', 'equipment', 'vehicle', 'other'].map((value) => ({
    value,
    labelKey: `custom.enums.asset_kind.${value}`,
  })),
  asset_condition: ['excellent', 'good', 'fair', 'poor', 'unserviceable'].map((value) => ({
    value,
    labelKey: `custom.enums.asset_condition.${value}`,
  })),
  asset_status: ['active', 'on_loan', 'under_repair', 'disposed'].map((value) => ({
    value,
    labelKey: `custom.enums.asset_status.${value}`,
  })),
  asset_rental_status: ['reserved', 'active', 'returned', 'cancelled'].map((value) => ({
    value,
    labelKey: `custom.enums.asset_rental_status.${value}`,
  })),
};

export const CustomReportFormPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const location = useLocation();
  const seededDraft = (location.state as { seededDraft?: SeededCustomReportDraft } | null)
    ?.seededDraft;
  const fromSeededDraft = Boolean(seededDraft && !isEdit);
  const { t } = useTranslation('reports');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const membership = useCurrentMembership();
  const isModuleEnabled = useChurchStore((state) => state.isModuleEnabled);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [objectSource, setObjectSource] = useState<CustomReportSourceKey | ''>('');
  const [columns, setColumns] = useState<string[]>([]);
  const [filters, setFilters] = useState<FilterRow[]>([]);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const existing = useQuery({
    queryKey: ['reports', 'custom', id],
    enabled: isEdit,
    queryFn: () => api.get<CustomReportDefinition>(`/reports/custom/${id}`),
  });

  useEffect(() => {
    if (!existing.data) return;
    setName(existing.data.name);
    setDescription(existing.data.description ?? '');
    setObjectSource(existing.data.objectSource);
    const source = getCustomReportSource(existing.data.objectSource);
    const allowed = new Set(
      (source?.fields ?? []).filter((field) => !field.filterOnly).map((field) => field.key),
    );
    setColumns(
      [
        ...new Set(
          existing.data.columns.map((key) => CUSTOM_REPORT_COLUMN_ALIASES[key] ?? key),
        ),
      ].filter((key) => allowed.has(key)),
    );
    setFilters(existing.data.filters);
    setSortField(existing.data.sortOrder?.field ?? '');
    setSortDirection(existing.data.sortOrder?.direction ?? 'asc');
  }, [existing.data]);

  useEffect(() => {
    if (!seededDraft || isEdit) return;
    setName(seededDraft.name);
    setDescription(seededDraft.description ?? '');
    setObjectSource(seededDraft.objectSource);
    setColumns(seededDraft.columns);
    setFilters(seededDraft.filters);
    setSortField(seededDraft.sortOrder?.field ?? '');
    setSortDirection(seededDraft.sortOrder?.direction ?? 'asc');
  }, [seededDraft, isEdit]);

  const availableSources = useMemo(
    () =>
      CUSTOM_REPORT_SOURCE_KEYS.filter((key) => {
        const source = getCustomReportSource(key);
        if (!source) return false;
        return isModuleEnabled(source.module) && hasPermission(membership, source.permission);
      }),
    [isModuleEnabled, membership],
  );

  const activeSource = objectSource ? getCustomReportSource(objectSource) : undefined;
  const activeFields = activeSource?.fields ?? [];
  const columnFields = activeFields.filter((field) => !field.filterOnly);

  const departments = useQuery({
    queryKey: ['people', 'departments', 'custom-report-filters'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; name: string }> }>('/people/departments?pageSize=100'),
    enabled: Boolean(objectSource),
  });

  const departmentFilterValue = filters.find(
    (row) => row.field === 'departmentId' && row.operator === 'eq',
  )?.value;

  const units = useQuery({
    queryKey: ['people', 'department-units', 'custom-report-filters', departmentFilterValue],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; name: string; label: string }> }>(
        `/people/department-units?pageSize=100&departmentId=${encodeURIComponent(departmentFilterValue ?? '')}`,
      ),
    enabled: Boolean(objectSource && departmentFilterValue),
  });

  const committees = useQuery({
    queryKey: ['groups', 'committees', 'custom-report-filters'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; name: string }> }>(
        '/groups?pageSize=100&type=manual&category=committee',
      ),
    enabled:
      Boolean(objectSource) &&
      isModuleEnabled('groups') &&
      hasPermission(membership, PERMISSIONS.GROUPS_VIEW),
  });

  useEffect(() => {
    if (!objectSource || isEdit || fromSeededDraft) return;
    setColumns(columnFields.map((field) => field.key));
    setFilters([]);
    setSortField('');
  }, [objectSource, columnFields, isEdit, fromSeededDraft]);

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        objectSource,
        columns,
        filters,
        sortOrder: sortField ? { field: sortField, direction: sortDirection } : null,
      };
      if (isEdit) return api.patch<CustomReportDefinition>(`/reports/custom/${id}`, payload);
      return api.post<CustomReportDefinition>('/reports/custom', payload);
    },
    onSuccess: (result) => {
      toast.success(isEdit ? t('custom.updated') : t('custom.saved'));
      navigate(`/reports/custom/${result.id}`);
    },
    onError: (error) => {
      if (error instanceof ApiError) toast.error(tErrors(error.code, error.params));
      else toast.error(tCommon('toast.somethingWentWrong'));
    },
  });

  const renderFilterValue = (filter: FilterRow, index: number, field?: CustomReportFieldDefinition) => {
    if (!field || ['empty', 'not_empty'].includes(filter.operator)) {
      return null;
    }

    if (field.type === 'date') {
      return (
        <DatePicker
          value={filter.value || null}
          onChange={(value) => {
            const next = [...filters];
            next[index] = { ...filter, value: value ?? '' };
            setFilters(next);
          }}
        />
      );
    }

    if (field.type === 'boolean') {
      return (
        <Listbox
          value={filter.value}
          onChange={(value) => {
            const next = [...filters];
            next[index] = { ...filter, value: value ?? '' };
            setFilters(next);
          }}
          options={[
            { value: 'true', label: t('custom.value.yes') },
            { value: 'false', label: t('custom.value.no') },
          ]}
        />
      );
    }

    if (field.valueSet && ENUM_OPTIONS[field.valueSet]) {
      const options = ENUM_OPTIONS[field.valueSet] ?? [];
      return (
        <Listbox
          value={filter.value}
          onChange={(value) => {
            const next = [...filters];
            next[index] = { ...filter, value: value ?? '' };
            setFilters(next);
          }}
          options={options.map((option) => ({
            value: option.value,
            label: t(option.labelKey, option.value),
          }))}
        />
      );
    }

    if (field.filterOptionSource === 'fund') {
      return (
        <FundSelect
          value={filter.value || null}
          onChange={(value) => {
            const next = [...filters];
            next[index] = { ...filter, value: value ?? '' };
            setFilters(next);
          }}
        />
      );
    }

    if (field.filterOptionSource === 'member') {
      return (
        <MemberFilterValue
          value={filter.value}
          onChange={(value) => {
            const next = [...filters];
            next[index] = { ...filter, value };
            setFilters(next);
          }}
        />
      );
    }

    if (field.filterOptionSource === 'department') {
      return (
        <Listbox
          value={filter.value}
          onChange={(value) => {
            const next = [...filters];
            next[index] = { ...filter, value: value ?? '' };
            for (const row of next) {
              if (row.field === 'unitId') row.value = '';
            }
            setFilters(next);
          }}
          options={(departments.data?.data ?? []).map((row) => ({
            value: row.id,
            label: row.name,
          }))}
          placeholder={t('filter.searchDepartment')}
        />
      );
    }

    if (field.filterOptionSource === 'unit') {
      const departmentSelected = Boolean(departmentFilterValue);
      return (
        <Listbox
          value={filter.value}
          onChange={(value) => {
            const next = [...filters];
            next[index] = { ...filter, value: value ?? '' };
            setFilters(next);
          }}
          options={
            departmentSelected
              ? (units.data?.data ?? []).map((row) => ({
                  value: row.id,
                  label: row.label ?? row.name,
                }))
              : []
          }
          placeholder={
            departmentSelected ? t('filter.searchUnit') : t('filter.selectDepartmentFirst')
          }
          disabled={!departmentSelected}
        />
      );
    }

    if (field.filterOptionSource === 'committee') {
      return (
        <Listbox
          value={filter.value}
          onChange={(value) => {
            const next = [...filters];
            next[index] = { ...filter, value: value ?? '' };
            setFilters(next);
          }}
          options={(committees.data?.data ?? []).map((row) => ({
            value: row.id,
            label: row.name,
          }))}
          placeholder={t('filter.searchCommittee')}
        />
      );
    }

    return (
      <Input
        value={filter.value}
        onChange={(event) => {
          const next = [...filters];
          next[index] = { ...filter, value: event.target.value };
          setFilters(next);
        }}
      />
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={isEdit ? t('custom.editTitle') : t('custom.newTitle')}
        description={isEdit ? t('custom.editDescription') : t('custom.newDescription')}
      />
      <ReportsSubnav />

      <p className="text-body-sm">
        <Link to="/reports/custom" className="inline-flex items-center gap-1 text-brand hover:underline">
          <ArrowLeft className="size-4" aria-hidden />
          {t('custom.backToList')}
        </Link>
      </p>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('custom.section.basics')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="report-name">{t('custom.nameLabel')}</Label>
              <Input id="report-name" value={name} onChange={(event) => setName(event.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="report-description">{t('custom.descriptionLabel')}</Label>
              <Textarea
                id="report-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                rows={3}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('custom.dataSource')}</Label>
              <Listbox
                value={objectSource}
                onChange={(value) => {
                  if (value) setObjectSource(value as CustomReportSourceKey);
                }}
                disabled={isEdit}
                placeholder={t('custom.chooseSource')}
                options={availableSources.map((key) => ({
                  value: key,
                  label: t(`custom.sources.${key}`, key),
                }))}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('custom.section.columns')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {columnFields.length > 0 ? (
              <>
                <MultiSelectListbox
                  value={columns}
                  onChange={setColumns}
                  options={columnFields.map((field) => ({
                    value: field.key,
                    label: t(`custom.fields.${field.key}`, field.key),
                  }))}
                  placeholder={t('custom.chooseColumns')}
                  summaryLabel={(count) => t('custom.columnsSelected', { count })}
                  selectAllLabel={t('custom.selectAllColumns')}
                  clearAllLabel={t('custom.clearAllColumns')}
                  removeLabel={tCommon('action.remove')}
                />
                {columns.length > 0 ? (
                  <ul className="flex flex-wrap gap-2" aria-label={t('custom.selectedColumns')}>
                    {columns.map((key) => (
                      <li
                        key={key}
                        className="flex max-w-full items-center gap-1 rounded-sm bg-surface-sunken px-2 py-1 text-body-sm text-fg"
                      >
                        <span className="truncate">{t(`custom.fields.${key}`, key)}</span>
                        <button
                          type="button"
                          aria-label={tCommon('action.remove')}
                          onClick={() => setColumns((previous) => previous.filter((item) => item !== key))}
                          className="rounded-sm p-0.5 text-fg-tertiary hover:text-fg"
                        >
                          <X className="size-3" aria-hidden />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-body-sm text-fg-muted">{t('custom.noColumns')}</p>
                )}
              </>
            ) : (
              <p className="text-body-sm text-fg-muted">{t('custom.chooseSourceFirst')}</p>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <CardTitle>{t('custom.section.filters')}</CardTitle>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={!activeFields.length}
              onClick={() => setFilters((previous) => [...previous, { field: '', operator: 'eq', value: '' }])}
            >
              <Plus className="size-4" aria-hidden />
              {t('custom.addFilter')}
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {filters.length === 0 ? (
              <p className="text-body-sm text-fg-muted">{t('custom.noFilters')}</p>
            ) : (
              filters.map((filter, index) => {
                const field = activeFields.find((item) => item.key === filter.field);
                const operators = field ? operatorsForField(field) : ['eq'];
                return (
                  <div key={index} className="grid gap-2 md:grid-cols-[1fr_1fr_1fr_auto] md:items-end">
                    <div className="space-y-1.5">
                      <Label>{t('custom.filterField')}</Label>
                      <Listbox
                        value={filter.field}
                        onChange={(value) => {
                          if (!value) return;
                          const next = [...filters];
                          const selected = activeFields.find((item) => item.key === value);
                          next[index] = {
                            field: value,
                            operator: selected ? operatorsForField(selected)[0] ?? 'eq' : 'eq',
                            value: '',
                          };
                          setFilters(next);
                        }}
                        options={activeFields.map((item) => ({
                          value: item.key,
                          label: t(`custom.fields.${item.key}`, item.key),
                        }))}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>{t('custom.filterOperator')}</Label>
                      <Listbox
                        value={filter.operator}
                        onChange={(value) => {
                          if (!value) return;
                          const next = [...filters];
                          next[index] = { ...filter, operator: value };
                          setFilters(next);
                        }}
                        options={operators.map((operator) => ({
                          value: operator,
                          label: t(`custom.operators.${operator}`, operator),
                        }))}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>{t('custom.filterValue')}</Label>
                      {renderFilterValue(filter, index, field)}
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      size="icon"
                      aria-label={tCommon('action.remove')}
                      onClick={() => setFilters((previous) => previous.filter((_, i) => i !== index))}
                    >
                      <X className="size-4" aria-hidden />
                    </Button>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>{t('custom.section.sort')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t('custom.sortField')}</Label>
              <Listbox
                value={sortField}
                onChange={(value) => setSortField(value ?? '')}
                placeholder={t('custom.noSort')}
                options={[
                  { value: '', label: t('custom.noSort') },
                  ...columnFields.map((field) => ({
                    value: field.key,
                    label: t(`custom.fields.${field.key}`, field.key),
                  })),
                ]}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('custom.sortDirection')}</Label>
              <Listbox
                value={sortDirection}
                onChange={(value) => {
                  if (value) setSortDirection(value as 'asc' | 'desc');
                }}
                disabled={!sortField}
                options={[
                  { value: 'asc', label: t('custom.sortAsc') },
                  { value: 'desc', label: t('custom.sortDesc') },
                ]}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={() => navigate('/reports/custom')}>
          {tCommon('action.cancel')}
        </Button>
        <Button
          onClick={() => void save.mutate()}
          disabled={!name.trim() || !objectSource || save.isPending}
          loading={save.isPending}
        >
          {isEdit ? tCommon('action.save') : t('custom.saveAction')}
        </Button>
      </div>
    </div>
  );
};
